import { useFormik } from "formik"
import { Button, CurrencyInput, Grid, Input, NumericInput, Switch, Typography } from "../../../components"
import currency from 'currency.js'
import { formatMoney, formatNumber, nl2br } from "@bussola-viagens/utils"
import { useContext, useEffect, useMemo, useState } from "react"
import { PriceSetting } from '@bussola-viagens/constants'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { AuthContext } from "../../../contexts"
import { useT, useProfileDisplay } from "../../../profile-i18n"
import { Alert, AlertTitle, Divider } from "@mui/material"
import { useAPI } from "../../../hooks"

interface ConnectMilesCalculatorProps {
    configuration: {
        prices: PriceSetting[],
        flight_in_next_seven_days: number
        comments?: string
    },
    costs: PriceSetting[]
}

export const ConnectMilesCalculator = ({ configuration, costs }: ConnectMilesCalculatorProps) => {
    const {me} = useContext(AuthContext)
    const t = useT()
    const { cur, foreign, fmt, fmtTyped, toBRL } = useProfileDisplay(configuration as any)
    const [currencies, setCurrencies] = useState<Array<{value: string, label: string, rate: number}>>([])
    const [currencyRates] = useAPI(`/currency`)
    const formik = useFormik({
        initialValues: {
            pax: 1,
            miles: '',
            fees: '',
            extra: '',
            currency: 'USD',
            fly_this_week: false
        },
        onSubmit: () => {}
    })

    const kPerPax = formik.values.pax ? currency(Number(formik.values.miles)).divide(1000).divide(Number(formik.values.pax)).value : 1

    const percentPerK = useMemo(() => {
            if (formik.values.fly_this_week) {
                return configuration.flight_in_next_seven_days
            }
    
            const price = configuration.prices.find(p => {
                if (!p.to && currency(Number(p.from)).divide(1000).value <= kPerPax) {
                    return true
                }
    
                return currency(Number(p.from)).divide(1000).value <= kPerPax && currency(Number(p.to)).divide(1000).value >= kPerPax
            })
    
            return price?.price || 0
        }, [formik.values.fly_this_week, configuration.flight_in_next_seven_days, configuration.prices, kPerPax])
    

    const fees = useMemo(() => {
        const rate = currencies.find(c => c.value === formik.values.currency)?.rate

        return currency(Number(formik.values.fees)).multiply(Number(rate || 1)).value
    }, [currencies, formik.values.currency, formik.values.fees])

    const bookingFee = useMemo(() => {
        const rate = currencies.find(c => c.value === formik.values.currency)?.rate

        return currency(Number(75)).multiply(Number(rate || 1)).value
    }, [currencies, formik.values.currency])
    
    const costPerK = useMemo(() => {
        const cost = costs.find(p => {
            if (!p.to && currency(Number(p.from)).divide(1000).value <= kPerPax) {
                return true
            }

            return currency(Number(p.from)).divide(1000).value <= kPerPax && currency(Number(p.to)).divide(1000).value >= kPerPax
        })

        return cost?.price || 0
    }, [configuration.flight_in_next_seven_days, configuration.prices, kPerPax, formik.values.fly_this_week, formik.values.miles, formik.values.pax, costs])
    
    const pricePerK = useMemo(() => {
        return currency(costPerK).multiply(currency(1).add(currency(percentPerK).divide(100))).value
    }, [percentPerK, costPerK])

    const totalCost = useMemo(() => {
        const k = currency(formik.values.miles).divide(1000).value
        const cost = currency(k).multiply(costPerK).value

        return currency(cost).add(fees || 0).add(toBRL(Number(formik.values.extra || 0))).value
    }, [pricePerK, formik.values.extra, fees, formik.values.miles])
    
    const totalPrice = useMemo(() => {
        const k = currency(formik.values.miles).divide(1000).value
        const cost = currency(k).multiply(pricePerK).value

        return currency(cost).add(fees || 0).add(toBRL(Number(formik.values.extra || 0))).add(formik.values.fly_this_week ? bookingFee : 0).value
    }, [pricePerK, formik.values.extra, fees, formik.values.miles, bookingFee, formik.values.fly_this_week])

    useEffect(() => {
        (async () => {
            const rates = Object.entries(currencyRates.data?.rates || {}).map(([key, value]) => {
                const rate = currency(1, {precision: 4}).divide(Number(value)).multiply(1 + (Number((configuration as any).exchange_markup ?? 7) / 100)).value

                return {
                    value: key,
                    label: `${key} (${fmt(rate)})`,
                    rate: Number(rate)
                }
            })

            setCurrencies(rates)
        })()
    }, [currencyRates])

    return (
        <Grid container spacing={1}>
             {configuration.comments && (<Grid item xs={12} mb={2}>
                <Alert icon={false} color="info">
                <AlertTitle>{t("Observações:")}</AlertTitle>
                    <span dangerouslySetInnerHTML={{__html: nl2br(configuration.comments)}} />
                </Alert>
            </Grid>)}
            <Grid item xs={12}>
                <Input 
                    name='pax'
                    label={t("Passageiros")}
                    onChange={(event) => {
                        formik.setFieldValue('pax', event.target.value.replace(/\D/ig, ''))
                    }}
                    value={formik.values.pax}
                />
            </Grid>
            <Grid item xs={12}>
                <NumericInput 
                    name='miles'
                    label={t("Milhas")}
                    onChange={formik.handleChange}
                    value={formik.values.miles}
                />
            </Grid>
            <Grid item xs={4}>
                <Input 
                    name="currency"
                    label={t("Moeda")}
                    select
                    options={currencies}
                    value={formik.values.currency}
                    onChange={formik.handleChange}
                    helperText={(foreign ? "Official rate + " : "Valor oficial + ") + String((configuration as any).exchange_markup ?? 7) + "%"}
                />
            </Grid>
            <Grid item xs={8}>
                <CurrencyInput
                    label={(foreign ? "Fees (" : "Taxas (") + formik.values.currency + ")"}
                    onChangeValue={(_, originalValue) => {
                        formik.setFieldValue('fees', originalValue, true)
                    }}
                    value={formik.values.fees}
                />
            </Grid>
            <Grid item xs={12}>
                <CurrencyInput
                    label={foreign ? ("Extras (" + cur + ")") : "Extras (R$)"}
                    onChangeValue={(_, originalValue) => {
                        formik.setFieldValue('extra', originalValue, true)
                    }}
                    value={formik.values.extra}
                    helperText={t("Utilize este campo para custos extras como bagagens e marcação de assentos.")}
                />
            </Grid>
            <Grid item xs={12}>
                <Switch
                    label={t("O voo é dentro dos próximos 7 dias?")}
                    onChange={() => {
                        formik.setFieldValue('fly_this_week', !formik.values.fly_this_week)
                    }}
                    value={formik.values.fly_this_week}
                />
            </Grid>
            {formik.values.fly_this_week && (<Grid item xs={12} mt={2}>
                <Typography fontSize={14} textAlign="center">
                    Taxa de Resgate: 75 USD ({fmt(bookingFee)})
                </Typography>
            </Grid>)}

            <Grid item xs={12} mt={2} display={"flex"} justifyContent={"space-evenly"}>
                <Typography textAlign="center" variant="body1" component={"span"}>
                    <strong>{t("Custo:")}</strong> {fmt(costPerK)}
                </Typography>
                <Typography textAlign="center" variant="body1" component={"span"}>
                    <strong>{t("Margem:")}</strong> {formatNumber(percentPerK)}%
                </Typography>
            </Grid>

            <Grid item xs={12} mt={2}>
                <Typography textAlign="center">
                    <strong>{t("Rentabilidade:")}</strong> ({currency(formik.values.miles).divide(1000).value} x {fmt(pricePerK)}) - ({currency(formik.values.miles).divide(1000).value} x {fmt(costPerK)}) = <strong>{fmt(currency(totalPrice).subtract(totalCost).value)}</strong>
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <Typography textAlign="center">
                    <strong>{t("Total:")}</strong> ({currency(formik.values.miles).divide(1000).value} x {fmt(pricePerK)}) {fees ? `+ ${fmt(Number(fees))}` : ''} {formik.values.extra ? `+ ${fmtTyped(Number(formik.values.extra))}` : ''} {formik.values.fly_this_week ? `+ ${fmt(bookingFee)}` : ''} = <strong>{fmt(totalPrice)}</strong>
                </Typography>
            </Grid>

            {me?.group.group_module?.is_offer_enabled && (<Grid item xs={12} mt={4} textAlign="center">
                <Button 
                    startIcon={<ReceiptLongIcon/>}
                    href={`/meus-orcamentos/novo?flight_price=${totalPrice}`}
                >
                    {t("Gerar Orçamento")}
                </Button>
            </Grid>)}
        </Grid>
    )
}